// src/components/PayoutsCard.jsx
import React from "react";
import { api } from "../lib/api";
import { getMyPromotions } from "../lib/apiPromotions";

const card = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  padding: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const left = {};
const title = { fontSize: 16, fontWeight: 600, color: "#111827", margin: 0 };
const sub = { fontSize: 13, color: "#6b7280", margin: "4px 0 0" };

const row = { display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" };

const badgeOk = {
  display: "inline-flex",
  alignItems: "center",
  height: 26,
  padding: "0 10px",
  borderRadius: 999,
  background: "#DCFCE7",
  color: "#065F46",
  border: "1px solid #86EFAC",
  fontSize: 12,
  fontWeight: 600,
};

const badgeWarn = {
  display: "inline-flex",
  alignItems: "center",
  height: 26,
  padding: "0 10px",
  borderRadius: 999,
  background: "#FEF3C7",
  color: "#92400E",
  border: "1px solid #FDE68A",
  fontSize: 12,
  fontWeight: 600,
};

const btn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 38,
  padding: "0 14px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#111827",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  textDecoration: "none",
  boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
  cursor: "pointer",
};

const btnGhost = {
  ...btn,
  background: "#fff",
  color: "#111827",
};

const promoCard = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  padding: 16,
  marginBottom: 16,
};

const promoTitle = { fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 };
const promoList = { margin: "8px 0 0", paddingLeft: 18, color: "#374151" };
const promoItem = { marginBottom: 6, fontSize: 13 };
const promoMuted = { fontSize: 13, color: "#6b7280", marginTop: 6 };

export default function PayoutsCard({ roleLabel = "Creator" }) {
  const [loading, setLoading] = React.useState(true);
  const [connected, setConnected] = React.useState(false);
  const [payoutsEnabled, setPayoutsEnabled] = React.useState(false);
  const [currentlyDue, setCurrentlyDue] = React.useState([]);
  const [promotions, setPromotions] = React.useState([]);
  const [promoError, setPromoError] = React.useState(false);

  async function refreshStatus() {
    setLoading(true);
    try {
      const s = await api.getJson("/payments/connect/status");
      setConnected(Boolean(s.connected));
      setPayoutsEnabled(Boolean(s.payouts_enabled));
      setCurrentlyDue(Array.isArray(s.currently_due) ? s.currently_due : []);
    } catch (e) {
      console.warn("connect_status failed:", e);
      setConnected(false);
      setPayoutsEnabled(false);
      setCurrentlyDue([]);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { refreshStatus(); }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getMyPromotions();
        const results = Array.isArray(data?.results) ? data.results : [];
        if (mounted) {
          setPromotions(results);
          setPromoError(false);
        }
      } catch (e) {
        if (mounted) {
          setPromotions([]);
          setPromoError(true);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  const normalizeRole = (role) =>
    String(role || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");

  const roleKey = normalizeRole(roleLabel) === "HOST" ? "HOST" : "ARTIST";
  const roleAliases =
    roleKey === "HOST"
      ? new Set(["HOST", "GALLERY"])
      : new Set(["ARTIST"]);

  const relevantPromos = promotions.filter((promo) => {
    const promoRole = normalizeRole(promo?.promotion?.eligible_role);
    // user_promotions are user-bound; if role is omitted, still show it.
    if (!promoRole) return true;
    return roleAliases.has(promoRole);
  });

  const formatDate = (value) => {
    if (!value) return "No expiration";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPromoLabel = (promo) => {
    if (promo?.promotion.commission_rate_override) {
      const percent = Number(promo.promotion.commission_rate_override) * 100;
      return `Platform fee: ${percent}% until ${formatDate(promo.expires_at)}`;
    }
    if (promo?.promotion.membership_fee_override_cents != null) {
      const cents = Number(promo.promotion.membership_fee_override_cents);
      const dollars = Number.isNaN(cents) ? promo.promotion.membership_fee_override_cents : (cents / 100).toFixed(2);
      return `Membership fee: $${dollars} until ${formatDate(promo.expires_at)}`;
    }
    return promo?.promotion.code || "Active promotion";
  };

  async function startOnboarding() {
    try {
      const { url } = await api.postJson("/payments/connect/onboard", {});
      window.location.href = url;
    } catch (e) {
      alert(`Failed to start onboarding: ${e.message || e}`);
    }
  }

  async function openDashboard() {
    try {
      const { url } = await api.postJson("/payments/connect/dashboard", {});
      window.open(url, "_blank", "noopener");
    } catch (e) {
      alert(`Failed to open Stripe dashboard: ${e.message || e}`);
    }
  }

  // Content states
  const needsAction = (currentlyDue || []).length > 0;

  return (
    <div>
      <div style={promoCard}>
        <h3 style={promoTitle}>Active Promotions</h3>
        {promoError ? (
          <p style={promoMuted}>Unable to load promotions right now.</p>
        ) : relevantPromos.length > 0 ? (
          <ul style={promoList}>
            {relevantPromos.map((promo) => (
              <li key={promo.promotion.code || promo.promotion.applied_at} style={promoItem}>
                {renderPromoLabel(promo)}
              </li>
            ))}
          </ul>
        ) : (
          <p style={promoMuted}>No active promotions.</p>
        )}
      </div>

      <div style={card}>
        <div style={left}>
          <h3 style={title}>{roleLabel} payouts</h3>
          {loading ? (
            <p style={sub}>Checking Stripe connection…</p>
          ) : connected ? (
            <>
              <p style={sub}>
                Your account is connected to Stripe. You can manage bank details and tax forms in the Stripe dashboard.
              </p>
              <div style={row}>
                <span style={badgeOk}>Payouts enabled</span>
                {needsAction && <span style={badgeWarn}>Action needed</span>}
              </div>
              {needsAction && (
                <p style={{ ...sub, marginTop: 8 }}>
                  Stripe still needs: <code>{currentlyDue.join(", ")}</code>
                </p>
              )}
            </>
          ) : (
            <>
              <p style={sub}>
                Set up payouts with Stripe to receive proceeds from your sales.
              </p>
              {needsAction && (
                <p style={{ ...sub, marginTop: 4 }}>
                  Action needed: <code>{currentlyDue.join(", ")}</code>
                </p>
              )}
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {!connected ? (
            <button style={btn} onClick={startOnboarding} disabled={loading}>
              Set up payouts
            </button>
          ) : (
            <>
              <button style={btnGhost} onClick={refreshStatus} disabled={loading}>
                Refresh
              </button>
              <button style={btn} onClick={openDashboard} disabled={loading || !payoutsEnabled}>
                Open Stripe dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
